import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { ProTable } from "@ant-design/pro-components";
import { Button, Input, ConfigProvider } from "antd";
import enUS from "antd/es/locale/en_US";
import api_address from "../../../constants/config";
import { ADMIN_COOKIE_NAME, LIMIT } from "../../../constants/admin";
import { GET_DATE_TIME_STRING_FROM_TIMESTAMP } from "../../../util/common";
import { JS_COOKIE } from "util/auth";
import { Container, AdminTableContainer } from "../styles";

interface CoachType {
  id: string;
  uuid: string;
  firstName: string;
  lastName: string;
  email: string;
  countrycode: string;
  time_zone: string;
  createdat: string;
  lastloginat: string;
}

interface SorterState {
  field: string;
  order: "ascend" | "descend" | undefined;
}

const Coach: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const [coachData, setCoachData] = useState<CoachType[]>([]);
  const [filteredData, setFilteredData] = useState<CoachType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [filters, setFilters] = useState<any>({});
  const [sorter, setSorter] = useState<SorterState | null>(null);
  const [searchText, setSearchText] = useState<string>("");

  const getCoachData = async (page: number, limit: number) => {
    setLoading(true);
    try {
      const { data } = await axios.get(api_address + "api/admin/coach", {
        headers: {
          Authorization: `Bearer ${JS_COOKIE.get(ADMIN_COOKIE_NAME)}`,
        },
        params: {
          page,
          limit,
        },
      });

      const sortedData = sortData(data.coach, sorter);
      setCoachData(sortedData);
      setFilteredData(sortedData);
      setTotal(data.rowCount);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    getCoachData(currentPage, pageSize);
  }, [currentPage, pageSize]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [filters, sorter, coachData, searchText]);

  const applyFiltersAndSort = () => {
    let result = [...coachData];

    if (searchText) {
      result = result.filter((item) =>
        Object.values(item).some((value) =>
          String(value).toLowerCase().includes(searchText.toLowerCase())
        )
      );
    }

    result = sortData(result, sorter);

    setFilteredData(result);
    setTotal(result.length);
  };

  const sortData = (data: CoachType[], currentSorter: SorterState | null) => {
    if (!currentSorter) return data;

    return [...data].sort((a, b) => {
      if (currentSorter.order === "ascend") {
        return a[currentSorter.field] > b[currentSorter.field] ? 1 : -1;
      } else if (currentSorter.order === "descend") {
        return a[currentSorter.field] < b[currentSorter.field] ? 1 : -1;
      }
      return 0;
    });
  };

  const renderDate = (text: string) => {
    if (!text || text === "-" || text.includes("NaN")) {
      return "-";
    }
    return GET_DATE_TIME_STRING_FROM_TIMESTAMP(text);
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const newSorter: SorterState = {
      field: sorter.field,
      order: sorter.order,
    };
    setSorter(newSorter);
    setFilters(filters);
    setPageSize(pagination.pageSize);
    setCurrentPage(pagination.current);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const columns = [
    {
      title: t("admin.coach.id"),
      dataIndex: "id",
      sorter: true,
    },
    {
      title: t("admin.coach.UUID"),
      dataIndex: "uuid",
      sorter: true,
    },
    {
      title: t("admin.coach.firstName"),
      dataIndex: "firstName",
      sorter: true,
    },
    {
      title: t("admin.coach.lastName"),
      dataIndex: "lastName",
      sorter: true,
    },
    {
      title: t("admin.coach.email"),
      dataIndex: "email",
      sorter: true,
    },
    {
      title: t("admin.coach.countryCode"),
      dataIndex: "countrycode",
      sorter: true,
    },
    {
      title: t("admin.coach.timeZone"),
      dataIndex: "time_zone",
      sorter: true,
    },
    {
      title: t("admin.coach.lastLoginAt"),
      dataIndex: "lastloginat",
      sorter: true,
      render: (_: any, record: CoachType) => renderDate(record.lastloginat),
    },
    {
      title: t("admin.coach.createdAt"),
      dataIndex: "createdat",
      sorter: true,
      render: (_: any, record: CoachType) => renderDate(record.createdat),
    },
  ];

  return (
    <ConfigProvider locale={enUS}>
      <AdminTableContainer>
        <Input.Search
          placeholder={"UUID, Name, Email"}
          onChange={handleSearch}
          style={{ marginBottom: 16 }}
        />
        <ProTable<CoachType>
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          pagination={{
            showQuickJumper: true,
            current: currentPage,
            total: total,
            pageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "25", "50", "100"],
          }}
          search={false}
          dateFormatter="string"
          headerTitle={t("admin.coach.title")}
          rowSelection={{
            onChange: (selectedRowKeys) => {
              setSelectedRowKeys(selectedRowKeys);
            },
          }}
          toolBarRender={() => [
            <Button
              key="create"
              onClick={() => history.push("/admin/coach/create")}
            >
              {t("admin.coach.create")}
            </Button>,
            selectedRowKeys.length === 1 && (
              <Button
                key="edit"
                onClick={() =>
                  history.push(`/admin/coach/edit/${selectedRowKeys[0]}`)
                }
              >
                {t("admin.coach.edit")}
              </Button>
            ),
          ]}
          onChange={handleTableChange}
          onRow={(record) => {
            return {
              onDoubleClick: () => {
                history.push(`/admin/coach/edit/${record.id}`);
              },
            };
          }}
        />
      </AdminTableContainer>
    </ConfigProvider>
  );
};

export default Coach;
